import { useState } from 'react';
import {
  Button,
  MessageBar,
  MessageBarBody,
  Card,
  makeStyles,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import { Info16Regular } from '@fluentui/react-icons';
import { Take5Submission, AppConfig } from '../types';
import { PhaseStrip } from './PhaseStrip';
import { TapRow } from './TapRow';
import { HOC_LEVELS } from '../services/constants';

interface Props {
  config: AppConfig;
  initial: Partial<Take5Submission['act']>;
  onNext: (data: Take5Submission['act']) => void;
  onShowInfo: (key: string) => void;
}

const useStyles = makeStyles({
  body: { flex: 1, overflowY: 'auto', paddingTop: '16px', paddingBottom: '20px', paddingLeft: '18px', paddingRight: '18px' },
  section: {
    marginBottom: '14px',
    paddingTop: '16px',
    paddingBottom: '16px',
    paddingLeft: '16px',
    paddingRight: '16px',
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow2,
    background: tokens.colorNeutralBackground1,
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  stepNum: {
    backgroundColor: '#2E7D32',
    color: '#FFFFFF',
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    flexShrink: 0,
  },
  infoBtn: { marginLeft: 'auto', minWidth: '44px', minHeight: '44px' },
  /*
   * Hierarchy of Control: single column.
   * Rationale: 2 columns at 50% each (~163px on iPhone) couldn't fit the
   * label "Substitution" + level circle + checkbox without truncation.
   * Single column reads more naturally as a ranked list anyway — the whole
   * point of "Hierarchy" is the order, top to bottom.
   */
  hocList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  hocLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
  },
  hocRank: {
    backgroundColor: '#FFFFFF',
    color: '#6B6B6B',
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: 700,
    flexShrink: 0,
  },
  ynRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: '12px',
    paddingBottom: '12px',
    paddingLeft: '4px',
    paddingRight: '4px',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
    fontSize: '13.5px',
    gap: '10px',
    lineHeight: 1.4,
    ':last-child': { borderBottomStyle: 'none' },
  },
  ynText: { flex: 1, minWidth: 0 },
  required: {
    color: '#E3272D',
    fontWeight: 700,
    marginLeft: '3px',
  },
  ynToggle: {
    display: 'flex',
    gap: '4px',
    flexShrink: 0,
  },
  ynBtn: {
    minWidth: '44px',
    minHeight: '44px',
    paddingLeft: '6px',
    paddingRight: '6px',
    borderRadius: '6px',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftColor: tokens.colorNeutralStroke1,
    backgroundColor: '#FFFFFF',
    fontSize: '13px',
    fontWeight: 700,
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  ynYon: {
    backgroundColor: '#2E7D32',
    color: '#FFFFFF',
    borderTopColor: '#2E7D32',
    borderRightColor: '#2E7D32',
    borderBottomColor: '#2E7D32',
    borderLeftColor: '#2E7D32',
  },
  ynNon: {
    backgroundColor: '#E3272D',
    color: '#FFFFFF',
    borderTopColor: '#E3272D',
    borderRightColor: '#E3272D',
    borderBottomColor: '#E3272D',
    borderLeftColor: '#E3272D',
  },
  ynNAon: {
    backgroundColor: '#6B6B6B',
    color: '#FFFFFF',
    borderTopColor: '#6B6B6B',
    borderRightColor: '#6B6B6B',
    borderBottomColor: '#6B6B6B',
    borderLeftColor: '#6B6B6B',
  },
  /* Inline warning panel matching the Review screen banners. */
  warnInline: {
    marginTop: '12px',
    borderRadius: '6px',
    paddingTop: '10px',
    paddingBottom: '10px',
    paddingLeft: '12px',
    paddingRight: '12px',
    fontSize: '13px',
    lineHeight: 1.45,
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    backgroundColor: '#FCE8E9',
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: '#E3272D',
    color: '#6F0F12',
  },
  warnIcon: {
    flexShrink: 0,
    fontSize: '16px',
    lineHeight: 1.2,
    marginTop: '1px',
  },
  warnBody: {
    flex: 1,
    minWidth: 0,
  },
  cta: { width: '100%', marginTop: '6px', minHeight: '52px', fontSize: '15px', fontWeight: 600 },
});

export function ActScreen({ config, initial, onNext, onShowInfo }: Props) {
  const styles = useStyles();
  const [hocSelected, setHocSelected] = useState<Set<string>>(
    new Set(initial.hierarchyOfControl || [])
  );
  const [regChecks, setRegChecks] = useState<Record<string, 'Y' | 'N' | 'NA'>>(
    initial.regulatoryChecks || {}
  );

  const toggleHoc = (label: string) => {
    setHocSelected(s => {
      const next = new Set(s);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  };
  const setReg = (id: string, val: 'Y' | 'N' | 'NA') => {
    setRegChecks(r => ({ ...r, [id]: val }));
  };

  const hasComplianceIssue = config.regChecks.some(c => c.required && regChecks[c.id] === 'N');

  const hasUnansweredRequired = config.regChecks.some(check => check.required && !regChecks[check.id]);

  const handleNext = () => {
    if (hasUnansweredRequired) return;
    onNext({
      hierarchyOfControl: Array.from(hocSelected),
      regulatoryChecks: regChecks,
    });
  };

  return (
    <>
      <PhaseStrip step={3} />
      <div className={styles.body} data-screen-body>
        <Card className={styles.section}>
          <div className={styles.sectionTitle}>
            <span className={styles.stepNum}>5</span>
            Hierarchy of Control
            <Button
              appearance="subtle"
              icon={<Info16Regular />}
              onClick={() => onShowInfo('hoc')}
              className={styles.infoBtn}
              aria-label="More info"
            />
          </div>
          <div style={{ fontSize: '13px', color: tokens.colorNeutralForeground3, marginBottom: 10 }}>
            Tick the <strong>highest practicable</strong> control. Start from the top.
          </div>
          <div className={styles.hocList}>
            {HOC_LEVELS.map(h => (
              <TapRow
                key={h.rank}
                variant="chip"
                checked={hocSelected.has(h.label)}
                onToggle={() => toggleHoc(h.label)}
              >
                <span className={styles.hocLabel}>
                  <span className={styles.hocRank}>{h.rank}</span>
                  {h.label}
                </span>
              </TapRow>
            ))}
          </div>
        </Card>

        <Card className={styles.section}>
          <div className={styles.sectionTitle}>
            <span className={styles.stepNum}>6</span>
            Regulatory checks
            <Button
              appearance="subtle"
              icon={<Info16Regular />}
              onClick={() => onShowInfo('regCheck')}
              className={styles.infoBtn}
              aria-label="More info"
            />
          </div>
          {config.regChecks.map(c => (
            <div key={c.id} className={styles.ynRow} role="group" aria-label={c.label}>
              <div className={styles.ynText}>
                {c.label}
                {c.required && <span className={styles.required}>*</span>}
              </div>
              <div className={styles.ynToggle}>
                <button
                  className={mergeClasses(styles.ynBtn, regChecks[c.id] === 'Y' && styles.ynYon)}
                  onClick={() => setReg(c.id, 'Y')}
                  aria-label="Yes"
                  aria-pressed={regChecks[c.id] === 'Y'}
                >Y</button>
                <button
                  className={mergeClasses(styles.ynBtn, regChecks[c.id] === 'N' && styles.ynNon)}
                  onClick={() => setReg(c.id, 'N')}
                  aria-label="No"
                  aria-pressed={regChecks[c.id] === 'N'}
                >N</button>
                {c.hasNA && (
                  <button
                    className={mergeClasses(styles.ynBtn, regChecks[c.id] === 'NA' && styles.ynNAon)}
                    onClick={() => setReg(c.id, 'NA')}
                    aria-label="Not applicable"
                    aria-pressed={regChecks[c.id] === 'NA'}
                  >N/A</button>
                )}
              </div>
            </div>
          ))}
          {hasComplianceIssue && (
            <div className={styles.warnInline}>
              <span className={styles.warnIcon} aria-hidden="true">⚠</span>
              <div className={styles.warnBody}>
                <strong>Stop · compliance issue.</strong> One or more high-risk checks are answered "No". You must resolve these before starting work. Contact your supervisor.
              </div>
            </div>
          )}
        </Card>

        {hasUnansweredRequired && <MessageBar intent="warning"><MessageBarBody>Answer each required regulatory check before continuing. You can report a failed check by selecting No.</MessageBarBody></MessageBar>}
        <Button
          disabled={hasUnansweredRequired}
          appearance="primary"
          size="large"
          onClick={handleNext}
          className={styles.cta}
        >
          Next · Review
        </Button>
      </div>
    </>
  );
}
